import { cn } from '@repo/ui/lib/utils'

export const SPACING = {
  gap: {
    sm: 'gap-2',
    md: 'gap-3',
    lg: 'gap-4',
  },
  stack: {
    xs: 'space-y-1',
    sm: 'space-y-2',
    md: 'space-y-3',
    lg: 'space-y-4',
  },
  inline: {
    xs: 'flex items-center gap-1',
    sm: 'flex items-center gap-2',
    md: 'flex items-center gap-3',
    lg: 'flex items-center gap-4',
  },
  wrap: {
    sm: 'flex flex-wrap items-center gap-2',
    md: 'flex flex-wrap items-center gap-3',
  },
  page: 'p-4 md:p-6',
  section: {
    sm: 'px-4 py-3',
    md: 'px-5 py-4 md:px-6 md:py-5',
    lg: 'px-6 py-5 md:px-7 md:py-6',
  },
  inset: 'p-4 md:p-5',
} as const

export const RADIUS = {
  soft: 'rounded-[2px]',
  card: 'rounded-[4px]',
  inset: 'rounded-[4px]',
  avatar: 'rounded-[4px]',
  pill: 'rounded-[3px]',
  panel: 'rounded-[4px]',
} as const

const panelSurface = cn(RADIUS.panel, 'border border-border/50 bg-background/95 shadow-none')
const panelSurfaceMuted = cn(
  RADIUS.panel,
  'border border-border/45 bg-background/85 shadow-none transition-[opacity,background-color,border-color]'
)
const insetSurface = cn(RADIUS.inset, 'border border-border/45 bg-muted/18')
const pillSurface = cn(RADIUS.pill, 'border border-border/50 bg-muted/18')
const previewFooterSurface = cn(
  RADIUS.panel,
  'mt-auto flex items-center justify-between gap-4 border-t border-border/55 bg-background/95 px-4 py-3'
)

export const SURFACE = {
  panel: panelSurface,
  panelMuted: panelSurfaceMuted,
  inset: insetSurface,
  pill: pillSurface,
  previewFooter: previewFooterSurface,
  previewCard: cn(RADIUS.panel, 'border-0 bg-background/72 shadow-none'),
  subtleCard: cn(RADIUS.panel, 'border-border/70 bg-background/90 py-0 shadow-none'),
  actionTile:
    'flex w-full items-center gap-4 border-dashed bg-background/55 px-4 py-4 text-left hover:bg-background',
  iconWell:
    'flex h-10 w-10 items-center justify-center rounded-full border border-border/70 bg-background',
  emptyState:
    'rounded-lg border border-dashed border-border/70 bg-background/90 px-4 py-5 text-sm text-muted-foreground',
  message: {
    default:
      'flex items-start gap-3 rounded-lg border border-border/70 bg-muted/25 p-4 text-sm text-muted-foreground',
    danger: 'flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4',
  },
  editorSection: cn(panelSurface, SPACING.section.lg),
  insetSection: cn(insetSurface, SPACING.inset),
} as const

export const LAYOUT = {
  grid: {
    twoColumn: 'grid gap-4 md:grid-cols-2',
    weighted: 'grid gap-4 md:grid-cols-[1.1fr_0.9fr]',
    identity: 'grid gap-4 lg:grid-cols-[auto_minmax(0,1.25fr)_minmax(0,1fr)] lg:items-start',
    coverageSplit: 'grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(21rem,0.95fr)]',
  },
  rows: {
    split: 'flex items-start justify-between gap-3',
    splitCenter: 'flex items-center justify-between gap-3',
    splitWrap: 'flex flex-wrap items-center justify-between gap-3',
    splitWrapTight: 'flex flex-wrap items-center justify-between gap-2',
    splitWrapTop: 'flex flex-wrap items-start justify-between gap-3',
    wrap: 'flex flex-wrap items-center gap-3',
    wrapTight: 'flex flex-wrap items-center gap-2',
    inlineTight: 'flex items-center gap-1',
  },
  actionRow: 'flex items-center gap-3',
} as const

export const FORM = {
  stepPanel: panelSurface,
  stepContent: 'space-y-6 px-5 py-0 md:px-6',
  stepContentTight: 'space-y-3 px-5 py-0 md:px-6',
  fieldStack: SPACING.stack.sm,
  fieldHint: 'text-xs text-muted-foreground',
  input: 'h-11 rounded-lg border-border/70 bg-background/95 shadow-none focus-visible:ring-2',
  textarea:
    'min-h-32 rounded-lg border-border/70 bg-background/95 px-3 py-3 shadow-none focus-visible:ring-2',
  selectTrigger:
    'h-11 w-full rounded-lg border-border/70 bg-background/95 shadow-none focus-visible:ring-2 data-[size=default]:h-11',
  selectContent: RADIUS.card,
  badge: 'rounded-[3px] px-2 py-1 text-[11px]',
  utilityPill: cn(pillSurface, 'px-3 py-2'),
  rows: {
    headerUtility: 'flex flex-wrap items-center justify-between gap-3',
    badge: 'flex flex-wrap gap-2',
    meta: SPACING.inline.sm,
    tags: 'flex flex-wrap items-center gap-2',
  },
  card: {
    contentReset: 'px-0 py-0',
    headerCompact: 'pb-3',
    headerReset: 'px-0 pb-4 pt-0',
    body: 'mt-0 flex min-h-0 min-w-0 flex-1 flex-col gap-4 pt-0',
  },
  panel: {
    compact: 'gap-4 pt-5 pb-1',
  },
  iconButton: 'h-7 w-7 px-0',
} as const

export const SHELL = {
  pagePadding: `relative mx-auto flex min-h-full w-full flex-col ${SPACING.page}`,
  header: {
    root: 'sticky top-0 z-20 mb-2 border-b border-border/70 bg-background/95 p-3 backdrop-blur-sm md:p-4',
    inner: `${SPACING.stack.md} md:${SPACING.stack.lg}`,
    brandRow: 'flex justify-start',
    brandPill:
      'inline-flex items-center gap-2 px-0 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-foreground',
    titleRow: 'flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4',
    titleCopy: 'min-w-0 flex-1 space-y-2',
    title: 'text-3xl font-semibold tracking-tight text-foreground md:text-4xl',
    description:
      'max-w-[56ch] text-sm leading-6 text-muted-foreground md:text-base md:leading-relaxed',
  },
  footer: {
    base: 'border-t border-border pt-1',
    pinned:
      'sticky bottom-0 z-20 shrink-0 bg-background/95 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur-sm md:px-6 md:pt-6 md:pb-6',
    compactButton: 'h-11 px-4 md:h-8 md:px-3',
  },
  preview: {
    aside: 'hidden xl:block',
    frame: 'sticky top-0 h-[100dvh] px-6 py-6',
    header: 'mb-4',
    label: 'text-[11px] uppercase tracking-[0.22em] text-muted-foreground',
    subLabel: 'mt-1 text-sm text-muted-foreground',
    body: 'h-[calc(100dvh-5.5rem)]',
  },
} as const

export const PREVIEW = {
  browser: {
    bar: 'flex flex-row items-center gap-2 px-1 py-2',
    dots: SPACING.inline.sm,
    address: 'h-8 flex-1 rounded-[4px] bg-muted/18 px-3 text-xs leading-8 text-muted-foreground',
  },
  stage: {
    shell: 'flex h-full flex-col gap-5 p-5 md:p-6',
    stack: 'flex min-h-0 flex-1 flex-col gap-4',
    heroRow: 'flex shrink-0 items-start justify-between gap-4 px-1',
    identityRow: 'flex min-w-0 items-center gap-3',
    titleStack: SPACING.stack.xs,
    meta: 'flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground',
    sectionGrid: 'grid flex-1 content-start gap-3 overflow-y-auto pt-0 md:grid-cols-2',
    sectionMeta: 'mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground',
    footerActions: 'flex shrink-0 items-center gap-2',
  },
  card: {
    frame: 'setup-preview-card h-full overflow-hidden border-0 bg-transparent py-0 shadow-none',
    shell: SURFACE.previewCard,
    selection: 'border py-0 shadow-none transition-colors duration-200',
    selectionBody: 'flex items-start justify-between gap-3 p-3',
    selectionSummary: 'flex flex-wrap items-center gap-2 text-xs text-muted-foreground md:text-sm',
    placeholder: 'border border-dashed border-border/60 bg-background/52 py-0 shadow-none',
    empty: SURFACE.emptyState,
  },
  calendar: {
    dateGrid: 'grid grid-cols-7 gap-2',
    slotGrid: 'grid grid-cols-3 gap-2',
    dateCell:
      'setup-preview-date-chip flex min-h-[96px] flex-col items-center justify-between border px-1 py-2 text-center transition-colors duration-200',
  },
  map: {
    shell:
      'setup-preview-card flex h-full min-h-[26rem] flex-col overflow-hidden border-0 bg-transparent shadow-none',
    stage: 'setup-preview-stage flex flex-1 flex-col p-4 md:p-6',
    frame: cn(
      RADIUS.panel,
      'relative h-full min-h-[26rem] overflow-hidden border border-border/70 bg-muted/20'
    ),
    summaryBar:
      'absolute left-3 top-3 z-10 flex max-w-[calc(100%-1.5rem)] flex-wrap items-center gap-2 rounded-[4px] border border-border/70 bg-background/92 px-3 py-2 text-xs text-muted-foreground shadow-[var(--app-shadow-soft)]',
    summaryBadgePrimary:
      'rounded-[3px] bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary',
    summaryBadgeMuted: 'rounded-[3px] bg-ring/10 px-2 py-1 text-[11px] font-medium text-foreground',
    headerBar:
      'absolute inset-x-3 top-3 z-10 flex items-center justify-between rounded-[4px] border border-border/70 bg-background/88 px-3 py-2 text-xs text-muted-foreground shadow-[var(--app-shadow-soft)] backdrop-blur-xl',
    loading:
      'flex h-full items-center justify-center bg-background/55 text-sm text-muted-foreground',
    loadingInset:
      'flex h-full items-center justify-center rounded-[4px] bg-background/55 text-sm text-muted-foreground',
    canvas: 'h-full min-h-[26rem] rounded-[inherit] border-0',
    canvasInset: 'h-full min-h-[28rem] rounded-none border-0',
  },
  footerBar: previewFooterSurface,
} as const

export const TIMELINE = {
  list: 'flex flex-col gap-3',
  dayCard: cn(
    panelSurfaceMuted,
    'grid gap-2 px-3 py-3 md:grid-cols-[6.25rem_minmax(0,1fr)] md:items-start md:gap-3 md:px-4 md:py-4'
  ),
  headerStack: 'space-y-2',
  headerRow: 'flex items-center justify-between gap-2',
  actionRow: 'flex items-center gap-2',
  iconButton: 'h-10 w-10 px-0 text-muted-foreground/75 hover:text-foreground md:h-7 md:w-7',
  body: 'min-w-0 transition-opacity',
  viewport:
    'scrollbar-hide overflow-x-auto overflow-y-hidden rounded-lg bg-background p-2 touch-pan-x overscroll-x-contain cursor-grab active:cursor-grabbing',
  canvas: 'relative h-[3.25rem] min-w-[var(--timeline-canvas-width)]',
  hourRail: 'pointer-events-none absolute inset-x-0 top-0 z-10 h-3 overflow-visible',
  hourLabel: 'absolute top-0 text-[9px] font-medium uppercase tracking-[0.08em] text-foreground/60',
  slotLayer: 'absolute inset-x-0 bottom-0 h-[2rem]',
  slotButton:
    'absolute top-0 bottom-0 border-l border-border/40 transition-colors first:border-l-0',
  slotStripe: 'bg-muted/[0.22]',
  slotHover: 'hover:bg-primary/8',
  window:
    'absolute top-1 bottom-1 z-20 rounded-md border border-primary/20 bg-primary/88 px-2 py-0 text-left text-[10px] font-medium text-primary-foreground shadow-[0_10px_24px_rgba(15,23,42,0.18)]',
  draftWindow:
    'absolute top-1 bottom-1 z-10 rounded-md border border-dashed border-primary/50 bg-primary/15',
} as const
